import React from 'react';
import ReactDOM from 'react-dom/client';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, split, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { toast } from 'sonner';
import { oidcConfig, getToken, setCurrentToken, clearToken } from './utils/auth';
import App from './App';
import './index.css';

const env = window._env_ || {};
const API_URL = env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || '/graphql';

// Derive WebSocket URL from HTTP URL
const wsUrl = API_URL.replace(/^http/, 'ws');

// HTTP link for queries and mutations
const httpLink = createHttpLink({
  uri: API_URL,
});

// Auth link — attaches Bearer token to every HTTP request
const authLink = setContext((_, { headers }) => {
  const token = getToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

// Error link — handle GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'UNAUTHENTICATED') {
        clearToken();
        toast.error('Session expired. Please sign in again.');
        return;
      }
      // The API authorises mutations by group. Say so plainly rather than
      // surfacing a bare message the user cannot act on.
      if (err.extensions?.code === 'FORBIDDEN') {
        const groups = err.extensions?.requiredGroups;
        toast.error(
          groups
            ? `Not permitted. Requires membership of: ${[].concat(groups).join(', ')}`
            : 'Not permitted for your account.',
        );
        return;
      }
      toast.error(err.message || 'An error occurred');
    }
  }
  if (networkError) {
    toast.error(`Network error: ${networkError.message}`);
  }
});

// Retry link — retry failed requests with exponential backoff
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 5000,
    jitter: true,
  },
  attempts: {
    max: 3,
    // Auth failures are terminal - retrying a 401/403 just triples the load and
    // delays the toast the user needs to see.
    retryIf: (error) =>
      !!error &&
      !error.message?.includes('UNAUTHENTICATED') &&
      !error.message?.includes('FORBIDDEN') &&
      !error.message?.includes('Not authorised'),
  },
});

// WebSocket link for subscriptions
const wsLink = new GraphQLWsLink(
  createClient({
    url: wsUrl,
    connectionParams: () => {
      const token = getToken();
      return token ? { authorization: `Bearer ${token}` } : {};
    },
  }),
);

// Split link: send subscriptions over WS, everything else over HTTP
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([errorLink, retryLink, authLink, httpLink]),
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Image: { keyFields: ['id'] },
      Blueprint: { keyFields: ['name'] },
      NotificationConfig: { keyFields: ['id'] },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});

// Called after OIDC redirect completes — store token in memory and clean URL
function onSigninCallback(user) {
  if (user?.access_token) {
    setCurrentToken(user.access_token);
  } else if (user?.id_token) {
    setCurrentToken(user.id_token);
  }
  // Navigate to home after successful sign-in
  window.history.replaceState({}, document.title, '/');
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
      <ApolloProvider client={client}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ApolloProvider>
    </AuthProvider>
  </React.StrictMode>,
);
